// frontend/src/utils/chartPeriods.js
//
// The wire's period string ↔ the chart's x value, and the bucket that string
// stands for. BarEnvelopeChart plots timeline payloads whose periods the
// backend emits in one of three shapes.
//
//   '2026-08'     month  — sales/inventory/demand timelines at month grain
//   '2026-W32'    week   — ISO week, the Monday that opens it
//   '2026-08-12'  day
//
// Every function here rejects a period it does not recognise.

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const WEEK_RE = /^(\d{4})-W(0[1-9]|[1-4]\d|5[0-3])$/;
const DAY_RE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

const SHAPES = [['month', MONTH_RE], ['week', WEEK_RE], ['day', DAY_RE]];

function bucketOrNull(period) {
  for (const [bucket, shape] of SHAPES) {
    if (shape.test(period)) return bucket;
  }
  return null;
}

/** 'month' | 'week' | 'day' — which bucket a wire period stands for. */
export function periodBucket(period) {
  const bucket = bucketOrNull(period);
  if (bucket == null) throw new Error(`Unrecognised period: ${JSON.stringify(period)}`);
  return bucket;
}

/** Whether a period is one this module can read. */
export function isReadablePeriod(period) {
  return bucketOrNull(period) != null;
}

/** The UTC instant a period opens on — the chart's x value. */
export function periodToDate(period) {
  switch (periodBucket(period)) {
    case 'month':
      return new Date(`${period}-01T00:00:00Z`);
    case 'week': {
      const [, year, week] = WEEK_RE.exec(period);
      const jan4 = new Date(Date.UTC(Number(year), 0, 4));
      const isoDayOfWeek = jan4.getUTCDay() || 7;
      const monday = new Date(jan4);
      monday.setUTCDate(jan4.getUTCDate() - isoDayOfWeek + 1 + (Number(week) - 1) * 7);
      return monday;
    }
    case 'day':
      return new Date(`${period}T00:00:00Z`);
  }
}

/** The x value of the bucket a calendar date ('2026-10-26') falls in. */
export function bucketOpeningOf(isoDate, bucket) {
  if (!DAY_RE.test(isoDate)) throw new Error(`Unrecognised date: ${JSON.stringify(isoDate)}`);
  const date = new Date(`${isoDate}T00:00:00Z`);
  switch (bucket) {
    case 'month':
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    case 'week':
      date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
      return date;
    case 'day':
      return date;
    default:
      throw new Error(`Unrecognised period bucket: ${JSON.stringify(bucket)}`);
  }
}

/** Which date fields a tick for this bucket carries, as `Intl.DateTimeFormat` options. */
export function periodLabelFields(bucket) {
  if (bucket === 'month') return { month: 'short', year: 'numeric' };
  if (bucket === 'week' || bucket === 'day') return { day: '2-digit', month: 'short' };
  throw new Error(`Unrecognised period bucket: ${JSON.stringify(bucket)}`);
}
